(function () {
  const root = document.querySelector('.shero-scorecard');
  if (!root) return;

  const scoreMap = { green: 2, yellow: 1, red: 0 };

  function statusFromAvg(avg) {
    if (avg <= 0.75) return "red";
    if (avg <= 1.5) return "yellow";
    return "green";
  }

  const blocks = [
    {
      key: "identity",
      title: "1) Identity and consent",
      gate: false,
      questions: [
        {
          key: "identity_recognition",
          title: "Can you reliably recognize returning customers across sessions?",
          actions: {
            red: "Improve identity capture (email/phone + login prompts) so you can suppress incentives, cap frequency, and measure repeat behavior.",
            yellow: "Standardize identifiers across Shopify + ESP (email/phone) and reduce “unknown” traffic where possible.",
            green: "Keep identity rules documented and audit consent + profile matching quarterly."
          }
        },
        {
          key: "identity_consent",
          title: "Are consent flags stored and enforced across email/SMS (and respected in targeting)?",
          actions: {
            red: "Implement consent governance (storage + enforcement) before scaling segmentation or omnichannel personalization.",
            yellow: "Fix gaps where consent is captured but not consistently applied across tools and campaigns.",
            green: "You’re ready for lifecycle orchestration and holdouts without compliance/consent risk."
          }
        }
      ]
    },
    {
      key: "events",
      title: "2) Events (behavior + outcomes)",
      gate: true,
      questions: [
        {
          key: "events_funnel",
          title: "Are core funnel events reliable (view, search, ATC, checkout started, purchase)?",
          actions: {
            red: "Fix instrumentation first: define one event spec and reconcile Shopify vs analytics discrepancies.",
            yellow: "Close mid-funnel gaps (search/checkout started) and document event definitions + deduping.",
            green: "You can safely test personalization changes and trust the direction of the results."
          }
        },
        {
          key: "events_returns",
          title: "Can you tie refunds/returns back to cohorts (by campaign/segment/test group)?",
          actions: {
            red: "Bring refunds/returns into reporting and connect outcomes back to cohorts/tests (at least weekly).",
            yellow: "Improve join logic and ensure return/refund outcomes are included as guardrail metrics in tests.",
            green: "You’re ready to report profit lift net of returns (not just revenue)."
          }
        }
      ]
    },
    {
      key: "product",
      title: "3) Product data (taxonomy + attributes)",
      gate: false,
      questions: [
        {
          key: "product_taxonomy",
          title: "Is your taxonomy consistent (collections/tags/metafields reflect how people shop)?",
          actions: {
            red: "Standardize taxonomy for your top categories and rebuild collection rules to be attribute-driven.",
            yellow: "Normalize the 80/20: clean taxonomy for top SKUs/categories before long-tail cleanup.",
            green: "You can build intent-aware merchandising and substitutes/complements reliably."
          }
        },
        {
          key: "product_inventory",
          title: "Are inventory/availability and key attributes accurate and usable in merchandising?",
          actions: {
            red: "Fix attribute completeness and inventory trust—don’t personalize shoppers into out-of-stocks or wrong fits.",
            yellow: "Standardize the attributes that drive discovery and cross-sell; tighten variant naming consistency.",
            green: "You can scale margin-aware sorting and guided selling experiences."
          }
        }
      ]
    },
    {
      key: "profit",
      title: "4) Profitability data (margin guardrails)",
      gate: true,
      questions: [
        {
          key: "profit_cogs",
          title: "Do you have SKU-level COGS or reliable margin bands (High/Med/Low)?",
          actions: {
            red: "Create margin visibility (COGS import or margin bands) before you scale offers, bundles, or recommendations.",
            yellow: "Automate margin updates and start using margin bands in merchandising and promo eligibility rules.",
            green: "You can optimize for gross margin $/visitor instead of revenue-only lifts."
          }
        },
        {
          key: "profit_guardrails",
          title: "Do you enforce margin guardrails in promotions (caps, exclusions, no stacking)?",
          actions: {
            red: "Implement guardrails: max discount, min margin thresholds, exclusions for low-margin SKUs, and promo suppression for high-intent cohorts.",
            yellow: "Tighten enforcement (stacking rules, exclusions, and suppression) and monitor discount rate as a guardrail metric.",
            green: "You’re ready to test incentives incrementally without training customers to wait for promos."
          }
        }
      ]
    },
    {
      key: "customer",
      title: "5) Customer data (LTV drivers + risk flags)",
      gate: false,
      questions: [
        {
          key: "customer_lifecycle",
          title: "Can you segment lifecycle reliably (new/active/at-risk/lapsed) using first order date + recency/frequency?",
          actions: {
            red: "Build lifecycle segments first; otherwise personalization will target everyone the same way.",
            yellow: "Improve lifecycle definitions (time windows, category repeat cycles) and align messaging to stages.",
            green: "You can scale lifecycle flows that improve time-to-second-purchase and repeat rate."
          }
        },
        {
          key: "customer_risk",
          title: "Do you have proxies for discount sensitivity and return risk at the customer level?",
          actions: {
            red: "Add simple risk flags so you can suppress incentives for high-intent/VIP and reduce return-driven profit leakage.",
            yellow: "Use risk flags as guardrails: cap discounts for promo-only cohorts; tighten fits for high-return segments.",
            green: "You can personalize offers with much lower risk of discount dependency or returns creep."
          }
        }
      ]
    },
    {
      key: "hygiene",
      title: "6) Channel hygiene (clean measurement)",
      gate: false,
      questions: [
        {
          key: "hygiene_utms",
          title: "Are UTMs consistent and persistent through checkout (so intent/source is usable)?",
          actions: {
            red: "Standardize UTM conventions and persistence before you trust segment performance.",
            yellow: "Harden UTM governance and reconcile channel reporting discrepancies.",
            green: "You can confidently personalize by intent/source and evaluate performance cleanly."
          }
        },
        {
          key: "hygiene_tests",
          title: "Can you prevent retargeting/email overlap from contaminating tests (true holdouts)?",
          actions: {
            red: "Create test exclusions for retargeting and implement a holdout protocol before running personalization experiments.",
            yellow: "Tighten exclusions and run tests long enough to cover purchase cycles (not just 7 days).",
            green: "You’re ready for incrementality testing (profit lift, not attribution credit)."
          }
        }
      ]
    }
  ];

  const grid = root.querySelector('[data-grid]');
  const resultsWrap = root.querySelector('[data-results]');
  const overallPill = root.querySelector('[data-overall-pill]');
  const overallScore = root.querySelector('[data-overall-score]');
  const summary = root.querySelector('[data-summary]');
  const actionsList = root.querySelector('[data-actions-list]');
  const tipsList = root.querySelector('[data-tips-list]');
  const toolsList = root.querySelector('[data-tools-list]');
  const errors = root.querySelector('[data-errors]');

  function opt(name, val, label, small) {
    return `
      <label class="opt" data-v="${val}">
        <input type="radio" name="${name}" value="${val}" aria-label="${label}"/>
        <span><strong>${label}</strong> <small>${small}</small></span>
      </label>
    `;
  }

  function renderBlocks() {
    grid.innerHTML = blocks.map(b => {
      const qs = b.questions.map(q => {
        const nm = `score_${b.key}_${q.key}`;
        return `
          <div class="q" data-q="${nm}">
            <p class="q-title">${escapeHtml(q.title)}</p>
            <div class="choices" role="radiogroup" aria-label="${escapeAttr(q.title)}">
              ${opt(nm, "green", "Green", "Ready")}
              ${opt(nm, "yellow", "Yellow", "Partial")}
              ${opt(nm, "red", "Red", "Missing")}
            </div>
          </div>
        `;
      }).join("");

      return `
        <div class="block" data-block="${b.key}">
          <h4>
            <span>${escapeHtml(b.title)}</span>
            <span class="badge" data-badge="${b.key}">Not scored</span>
          </h4>
          ${qs}
        </div>
      `;
    }).join("");

    grid.querySelectorAll('input[type="radio"]').forEach(r => {
      r.addEventListener('change', updateBlockBadgeFromInputs);
    });
  }

  function updateBlockBadgeFromInputs() {
    for (const b of blocks) {
      const vals = b.questions.map(q => {
        const nm = `score_${b.key}_${q.key}`;
        const picked = root.querySelector(`input[name="${nm}"]:checked`);
        return picked ? picked.value : null;
      });

      const badge = root.querySelector(`[data-badge="${b.key}"]`);
      if (!badge) continue;

      if (vals.some(v => v === null)) {
        badge.className = "badge";
        badge.textContent = "Not scored";
        continue;
      }

      const avg = vals.reduce((s, v) => s + scoreMap[v], 0) / vals.length;
      const st = statusFromAvg(avg);
      badge.className = "badge " + (st === "green" ? "ok" : st === "yellow" ? "warn" : "danger");
      badge.textContent = st.charAt(0).toUpperCase() + st.slice(1);
    }
  }

  function getSelections() {
    const selections = {};
    for (const b of blocks) {
      selections[b.key] = {};
      for (const q of b.questions) {
        const nm = `score_${b.key}_${q.key}`;
        const picked = root.querySelector(`input[name="${nm}"]:checked`);
        selections[b.key][q.key] = picked ? picked.value : null;
      }
    }
    return selections;
  }

  function calculate() {
    errors.textContent = "";
    const sel = getSelections();

    const missing = [];
    for (const b of blocks) {
      for (const q of b.questions) {
        if (!sel[b.key][q.key]) missing.push({ block: b.key, q: q.key });
      }
    }
    if (missing.length) {
      resultsWrap.hidden = true;
      errors.textContent = "Please answer all questions before calculating results.";
      const first = root.querySelector(`[data-block="${missing[0].block}"]`);
      first && first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const totalQuestions = blocks.reduce((n, b) => n + b.questions.length, 0);
    const max = totalQuestions * 2;
    let total = 0;

    const blockSummaries = {};
    let hardGateTriggered = false;

    for (const b of blocks) {
      let blockSum = 0;
      let anyRed = false;
      for (const q of b.questions) {
        const v = sel[b.key][q.key];
        blockSum += scoreMap[v];
        total += scoreMap[v];
        if (v === "red") anyRed = true;
      }
      const avg = blockSum / b.questions.length;
      const st = statusFromAvg(avg);
      blockSummaries[b.key] = { avg, status: st, anyRed };

      if (b.gate && anyRed) hardGateTriggered = true;
    }

    const pct = Math.round((total / max) * 100);

    let overall = "green";
    if (pct <= 40) overall = "red";
    else if (pct <= 75) overall = "yellow";
    else overall = "green";

    if (hardGateTriggered) overall = "red";

    const priorityActions = [];
    for (const b of blocks) for (const q of b.questions) if (sel[b.key][q.key] === "red") priorityActions.push(q.actions.red);
    for (const b of blocks) for (const q of b.questions) if (sel[b.key][q.key] === "yellow") priorityActions.push(q.actions.yellow);
    if (!priorityActions.length) priorityActions.push("You’re in a strong position. Pick 2–3 use cases, run holdouts, and optimize for gross margin $/visitor with returns guardrails.");

    const tips = buildTips(overall, hardGateTriggered);
    const tools = buildToolGuidance(overall, hardGateTriggered, blockSummaries);

    setOverallPill(overall, pct, total, max, hardGateTriggered);
    summary.innerHTML = buildSummary(overall, pct, blockSummaries, hardGateTriggered);

    actionsList.innerHTML = unique(priorityActions).slice(0, 7).map(t => `<li>${escapeHtml(t)}</li>`).join("");
    tipsList.innerHTML = tips.map(t => `<li>${escapeHtml(t)}</li>`).join("");
    toolsList.innerHTML = tools.map(t => `<li>${escapeHtml(t)}</li>`).join("");

    resultsWrap.hidden = false;
    resultsWrap.scrollIntoView({ behavior: "smooth", block: "start" });

    updateBlockBadgeFromInputs();
  }

  function buildTips(overall) {
    if (overall === "red") {
      return [
        "Stop tool shopping until Events and Profitability are stable. Tools will amplify noise and push you toward discount-led ‘wins.’",
        "Define your primary metric as gross margin $/visitor (or contribution margin $/visitor) and treat return rate + discount rate as guardrails.",
        "Start with rules-based personalization (segmentation, suppression, merchandising) to establish clean baselines.",
        "Only after the foundation is stable: pick 2–3 high-impact use cases (repeat purchase, margin mix, returns reduction)."
      ];
    }
    if (overall === "yellow") {
      return [
        "Focus on governance + measurement next: holdouts, exclusions (retargeting), and profit-based reporting.",
        "Prioritize one use case at a time (e.g., post-purchase cross-sell with margin bands) and validate incrementality before scaling.",
        "Use suppression rules to protect margin (avoid incentivizing customers who would buy anyway).",
        "If you add a tool, choose one that improves experimentation/measurement before adding more onsite widgets."
      ];
    }
    return [
      "Pick 2–3 priority use cases tied to margin and LTV (repeat purchase, margin mix, returns reduction) and ship in controlled increments.",
      "Require a true control group (holdout) and report gross margin $/visitor alongside return rate and discount rate guardrails.",
      "Scale tooling only where it replaces manual work or improves decisioning quality (search/merchandising, lifecycle orchestration, testing).",
      "Avoid celebrating ‘engagement’ metrics without profit lift—optimize for net outcomes."
    ];
  }

  function buildToolGuidance(overall, hardGate) {
    if (hardGate) {
      return [
        "Avoid buying recommendation/personalization widgets right now—fix Events + Profitability first.",
        "If you invest in tooling, prioritize instrumentation, reporting, and experimentation infrastructure (holdouts, exclusions).",
        "Use rules-based merchandising and lifecycle segmentation as your baseline until gated areas are Green."
      ];
    }
    if (overall === "red") {
      return [
        "Prioritize data quality and profit visibility over vendor demos (identity, events, taxonomy, margin bands).",
        "Choose tools that improve measurement and governance, not tools that only change frontend content.",
        "Delay promotions/personalization tools that push discounting without margin guardrails."
      ];
    }
    if (overall === "yellow") {
      return [
        "Consider experimentation/measurement tooling before scaling onsite personalization widgets.",
        "Add recommendation engines only for one priority use case and require holdouts + profit reporting.",
        "If discounting is involved, enforce max discount and minimum margin thresholds; include return rate as a guardrail."
      ];
    }
    return [
      "You can evaluate tools aligned to your top use cases (search/merchandising, recommendations, lifecycle orchestration).",
      "Require incrementality (holdouts) and reporting on gross margin $/visitor + return-rate and discount-rate guardrails.",
      "Avoid tools that only report attribution-based ‘lifts’ without a true control group."
    ];
  }

  function buildSummary(overall, pct, blockSummaries, hardGate) {
    const names = {
      identity: "Identity and consent",
      events: "Events",
      product: "Product data",
      profit: "Profitability data",
      customer: "Customer data",
      hygiene: "Channel hygiene"
    };

    const sorted = Object.entries(blockSummaries)
      .sort((a, b) => a[1].avg - b[1].avg)
      .map(([k]) => names[k]);

    const weakest = sorted.slice(0, 3).join(", ");

    if (overall === "red") {
      return `
        <strong>Result:</strong> You are not ready to scale profit-first personalization yet.
        ${hardGate ? "<br><strong>Hard gate triggered:</strong> Events and/or Profitability include Red answers, which invalidates most ‘lift’ reporting." : ""}
        <br><strong>Fix first:</strong> ${escapeHtml(weakest)}.
        <br><strong>Goal:</strong> stabilize tracking + margin visibility so you can measure gross margin $/visitor net of returns.
      `;
    }
    if (overall === "yellow") {
      return `
        <strong>Result:</strong> You can run targeted personalization, but measurement and guardrails need tightening.
        <br><strong>Biggest opportunities:</strong> ${escapeHtml(weakest)}.
        <br><strong>Goal:</strong> validate incrementality with holdouts and protect margin (discount/returns guardrails) before scaling.
      `;
    }
    return `
      <strong>Result:</strong> You’re ready to scale profit-first personalization.
      <br><strong>Next step:</strong> pick 2–3 use cases and run holdouts with gross margin $/visitor as the primary metric.
      <br><strong>Watchouts:</strong> keep return rate and discount rate as guardrails.
    `;
  }

  function setOverallPill(overall, pct, total, max, hardGate) {
    overallPill.className = "pill " + (overall === "green" ? "ok" : overall === "yellow" ? "warn" : "danger");
    overallPill.textContent = "Overall readiness: " + overall.toUpperCase();
    overallScore.textContent = `Aggregated score: ${total}/${max} (${pct}%)${hardGate ? " • hard gate applied" : ""}`;
  }

  function reset() {
    resultsWrap.hidden = true;
    errors.textContent = "";
    root.querySelectorAll('input[type="radio"]').forEach(r => r.checked = false);
    blocks.forEach(b => {
      const badge = root.querySelector(`[data-badge="${b.key}"]`);
      if (badge) { badge.className = "badge"; badge.textContent = "Not scored"; }
    });
  }

  function print() {
    const parent = root.parentNode;
    const placeholder = document.createComment('shero-scorecard-placeholder');
    parent.insertBefore(placeholder, root);
    document.body.appendChild(root);
    document.body.classList.add('shero-scorecard-printing');

    const cleanup = () => {
      if (placeholder.parentNode) {
        placeholder.parentNode.insertBefore(root, placeholder);
        placeholder.remove();
      }
      document.body.classList.remove('shero-scorecard-printing');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);

     window.print();
  }

  function unique(arr) {
    const seen = new Set();
    return arr.filter(x => (seen.has(x) ? false : (seen.add(x), true)));
  }
  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, s => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[s]));
  }
  function escapeAttr(str) {
    return escapeHtml(str).replace(/`/g, '&#96;');
  }

  root.querySelector('[data-action="calculate"]').addEventListener('click', calculate);
  root.querySelector('[data-action="reset"]').addEventListener('click', reset);
  root.querySelector('[data-action="print"]').addEventListener('click', print);

  renderBlocks();
})();
